import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RegionResponseDto } from "@/types/admin-region";
import { humanizeDuration } from "../utils/humanizeDuration";

interface RegionDetailDialogProps {
  region: RegionResponseDto | null;
  onClose: () => void;
}

interface FieldRowProps {
  label: string;
  value: React.ReactNode;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, value }) => (
  <div className="grid grid-cols-[160px_1fr] gap-3 py-1.5 border-b border-base-border last:border-0">
    <dt className="text-xs uppercase tracking-wide text-muted-foreground">
      {label}
    </dt>
    <dd className="text-sm text-foreground break-all">{value}</dd>
  </div>
);

export const RegionDetailDialog: React.FC<RegionDetailDialogProps> = ({
  region,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!region) return null;

  const storage = region.storage;
  const presign = region.storage.presignExpirySeconds;
  const presignFormatted = t("regions.detail.presignExpirySeconds", {
    seconds: presign,
    human: humanizeDuration(presign, t),
  });

  return (
    <Dialog open={!!region} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("regions.detail.title")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-foreground">
              {t("regions.detail.sections.identity")}
            </h3>
            <dl className="flex flex-col">
              <FieldRow
                label={t("regions.detail.fields.code")}
                value={<span className="font-mono text-xs">{region.code}</span>}
              />
              <FieldRow
                label={t("regions.detail.fields.name")}
                value={region.name}
              />
              <FieldRow
                label={t("regions.detail.fields.status")}
                value={
                  <Badge
                    variant={region.active ? "new_default" : "new_secondary"}
                  >
                    {region.active
                      ? t("regions.status.active")
                      : t("regions.status.inactive")}
                  </Badge>
                }
              />
            </dl>
          </section>
          <section className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-foreground">
              {t("regions.detail.sections.storage")}
            </h3>
            <dl className="flex flex-col">
              <FieldRow
                label={t("regions.detail.fields.storageType")}
                value={t(`regions.storageType.${storage.type}`)}
              />
              <FieldRow
                label={t("regions.detail.fields.bucket")}
                value={
                  <span className="font-mono text-xs">{storage.bucket}</span>
                }
              />
              {storage.endpoint ? (
                <FieldRow
                  label={t("regions.detail.fields.endpoint")}
                  value={
                    <span className="font-mono text-xs">
                      {storage.endpoint}
                    </span>
                  }
                />
              ) : null}
              {storage.port !== null ? (
                <FieldRow
                  label={t("regions.detail.fields.port")}
                  value={
                    <span className="font-mono text-xs">{storage.port}</span>
                  }
                />
              ) : null}
              <FieldRow
                label={t("regions.detail.fields.useSSL")}
                value={
                  storage.useSSL
                    ? t("regions.detail.yes")
                    : t("regions.detail.no")
                }
              />
              {storage.regionCode ? (
                <FieldRow
                  label={t("regions.detail.fields.regionCode")}
                  value={
                    <span className="font-mono text-xs">
                      {storage.regionCode}
                    </span>
                  }
                />
              ) : null}
              <FieldRow
                label={t("regions.detail.fields.presignExpiry")}
                value={presignFormatted}
              />
              {storage.publicEndpoint ? (
                <FieldRow
                  label={t("regions.detail.fields.publicEndpoint")}
                  value={
                    <span className="font-mono text-xs">
                      {storage.publicEndpoint}
                    </span>
                  }
                />
              ) : null}
            </dl>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};
